import { makeArticle, verifiedProviderRelated } from "./learnFactory";

export const pageIsSaintCoveredByInsurance = makeArticle({
  slug: "is-saint-tms-covered-by-insurance",
  title: "Is SAINT TMS Covered by Insurance? | Neuro Wellness",
  description:
    "SAINT TMS insurance coverage is limited but growing. Learn how Medicare, Blue Cross Blue Shield, and commercial plans handle SAINT, how prior authorization works, and when self-pay is faster.",
  canonical: "/learn/is-saint-tms-covered-by-insurance",
  h1: "Is SAINT TMS Covered by Insurance?",
  breadcrumbLabel: "Is SAINT TMS Covered by Insurance?",
  shortAnswer:
    "Sometimes, but coverage is still limited. Medicare Fee-for-Service pays for SAINT® at Verified SAINT Provider hospital sites. Medicare Advantage, commercial, and Medicaid plans decide case by case through prior authorization. Some plans, including certain Blue Cross Blue Shield plans, have begun covering accelerated TMS. At Neuro Wellness TMS Centers of America, we accept Blue Cross Blue Shield and the BCBS Federal Employee Program in select states. Most of our SAINT patients choose self-pay because it's faster.",
  keyTakeaways: [
    "SAINT® coverage is still limited but expanding as more plans add accelerated TMS policies.",
    "Medicare Fee-for-Service pays for SAINT® at Verified SAINT Provider hospital sites.",
    "SAINT® uses newer Category III CPT codes (0889T–0892T), often reviewed individually.",
    "Neuro Wellness accepts Blue Cross Blue Shield and the BCBS Federal Employee Program in select states.",
    "Self-pay is the most common option because it is faster — consultation to treatment in days.",
  ],
  sections: [
    {
      heading: "Why SAINT coverage is different from standard TMS",
      paragraphs: [
        "Standard TMS has been FDA-cleared since 2008, and most major insurers now cover it for treatment-resistant depression. [[SAINT|/learn/what-is-saint-treatment-for-depression]] was cleared by the FDA in 2022, and insurers are still catching up. SAINT claims use newer CPT codes (0889T through 0892T), which are Category III codes for emerging technology. Many plans review these individually rather than covering them automatically.",
      ],
    },
    {
      heading: "Coverage by plan type",
      table: {
        headers: ["Plan type", "How SAINT is handled"],
        rows: [
          [
            "Medicare Fee-for-Service",
            "Approved for payment at Verified SAINT Provider hospital sites",
          ],
          ["Medicare Advantage", "Case by case through prior authorization"],
          [
            "Commercial insurance",
            "Case by case; some plans now cover accelerated TMS",
          ],
          ["Medicaid", "Case by case through prior authorization"],
          [
            "Self-pay",
            "Available at all Verified SAINT Providers; [[pricing|/learn/saint-tms-cost]] varies by site",
          ],
        ],
      },
      paragraphsAfter: [
        "Coverage is expanding. In 2026, Premera Blue Cross updated its policy to cover accelerated TMS including SAINT's 10-sessions-per-day schedule, and Highmark Blue Cross Blue Shield added accelerated TMS to its policy. Other plans are reviewing it.",
      ],
    },
    {
      heading: "Insurance at Neuro Wellness TMS Centers of America",
      paragraphs: ["We offer two ways to access SAINT:"],
      list: [
        "Select insurance plans. We accept Blue Cross Blue Shield in select states and the Blue Cross Blue Shield Federal Employee Program (FEP) in select states. Because insurance requires review and approval, this path generally takes longer from consultation to treatment. Our team handles the paperwork and guides you through each step.",
        "Self-pay (our most common option). Self-pay offers the most speed and flexibility. Most patients move from consultation to treatment within a few days, with appointments seven days a week.",
      ],
    },
    {
      heading: "What insurers usually want to see",
      paragraphs: [
        "If your plan reviews SAINT through prior authorization, expect them to ask for documentation like:",
      ],
      list: [
        "A diagnosis of major depressive disorder",
        "A record of antidepressant trials that didn't produce enough improvement in the current episode",
        "Depression rating scores such as the PHQ-9",
        "Any psychotherapy you've tried",
        "Confirmation you have no contraindications, such as implanted metal devices",
      ],
      paragraphsAfter: [
        "Gathering your medication history before your consultation speeds this up.",
      ],
    },
    {
      heading: "What to do next",
      paragraphs: [
        "You don't have to figure out your coverage alone. Call us with your insurance card in hand. We'll tell you whether your plan is one we work with, what the approval process looks like, and how self-pay compares on timing.",
      ],
      links: [
        {
          to: "/contactus",
          text: "Check your coverage and schedule a free consultation",
        },
        { to: "/saint", text: "Learn more about SAINT" },
      ],
    },
  ],
  faqs: [
    {
      question: "Does Blue Cross Blue Shield cover SAINT?",
      answer:
        "Some BCBS plans do, depending on the state and plan. We accept BCBS and the BCBS Federal Employee Program in select states. Call us to check your specific plan.",
    },
    {
      question: "Does Medicare cover SAINT?",
      answer:
        "Medicare Fee-for-Service pays for SAINT at Verified SAINT Provider hospital sites. Medicare Advantage plans decide case by case.",
    },
    {
      question: "If my plan covers standard TMS, does it cover SAINT?",
      answer:
        "Not necessarily. SAINT uses different billing codes and is often reviewed separately.",
    },
    {
      question: "Can I use my HSA or FSA for SAINT?",
      answer:
        "SAINT is an FDA-cleared medical treatment, so it's generally an eligible expense. Confirm with your plan administrator.",
    },
    {
      question: "What if my claim is denied?",
      answer:
        "Denials can sometimes be appealed. Self-pay remains available if you don't want to wait.",
    },
  ],
  sources: [
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "American Medical Association. CPT® Category III Codes 0889T–0892T.",
      url: "https://www.ama-assn.org",
    },
  ],
  relatedLinks: verifiedProviderRelated,
});
